'use client'

import ProductCard from '../cards/ProductCard'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Scrollbar, Mousewheel, FreeMode } from 'swiper/modules'
import { useRef } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import type { INewArrivalsAPIResponseDataEntity } from '@/src/types/newArrivals'
import type { IRecentlyViewedProductAPIResponseDataEntity } from '@/src/types/recentlyViewedProduct'

interface ProductSlidersProps {
    title: string
    subTitle: string
    products: (INewArrivalsAPIResponseDataEntity | IRecentlyViewedProductAPIResponseDataEntity)[]
    description?: string
    recentlyViewed?: boolean
}

export default function ProductSliders({ title, subTitle, products, description, recentlyViewed }: ProductSlidersProps) {
    const headerRef = useRef<HTMLDivElement>(null)
    const sectionRef = useRef<HTMLDivElement>(null)

    gsap.registerPlugin(useGSAP, ScrollTrigger)

    useGSAP(() => {
        if (recentlyViewed) return

        gsap.from('.reveal-text', {
            yPercent: 100,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power3.out',
            scrollTrigger: {
                trigger: headerRef.current,
                start: 'top 85%',
                toggleActions: 'play none none reverse',
            },
        })
    }, { scope: headerRef, dependencies: [recentlyViewed] })

    useGSAP(() => {
        if (recentlyViewed) return

        const mm = gsap.matchMedia()

        // Desktop only — scrub transforms on the slider wrapper break touch momentum on mobile
        mm.add('(min-width: 768px)', () => {
            gsap.from(sectionRef.current, {
                opacity: 0.3,
                x: 200,
                scale: 0.95,
                ease: 'none',
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: 'top 85%',
                    end: 'top 40%',
                    scrub: 1,
                },
            })
        })

        return () => mm.revert()
    }, { scope: sectionRef, dependencies: [recentlyViewed] })

    return (
        <>
            {products && products?.length > 0 && <div ref={headerRef} className="custom_container">
                <h2 className="secondary_heading text-center">
                    <span className="overflow-hidden inline-block">
                        <span className="reveal-text inline-block font-semibold!">{title}</span>
                    </span>{' '}
                    <span className="overflow-hidden inline-block">
                        <span className="reveal-text inline-block font-secondary italic">{subTitle}</span>
                    </span>
                </h2>
                {description && (
                    <div className="overflow-hidden">
                        <p className="reveal-text max-w-3xl mx-auto text-center font-light opacity-80 text-sm md:text-base mt-2">{description}</p>
                    </div>
                )}
            </div>}
            <div className="overflow-hidden overscroll-x-contain">
                <div ref={sectionRef} className="pl-[5vw]">
                    <Swiper
                        modules={[Scrollbar, Mousewheel, FreeMode]}
                        spaceBetween={16}
                        speed={400}
                        grabCursor
                        touchStartPreventDefault={false}
                        touchReleaseOnEdges
                        threshold={5}
                        resistanceRatio={0.85}
                        scrollbar={{ draggable: true }}
                        mousewheel={{ forceToAxis: true, releaseOnEdges: true, sensitivity: 1.2 }}
                        freeMode={{
                            enabled: true,
                            momentum: true,
                            momentumRatio: 1,
                            momentumVelocityRatio: 1,
                            minimumVelocity: 0.02,
                            momentumBounce: false,
                        }}
                        breakpoints={{
                            0: { slidesPerView: 1.5, spaceBetween: 12 },
                            640: { slidesPerView: 2.2, spaceBetween: 16 },
                            768: { slidesPerView: 2.8, spaceBetween: 16 },
                            1024: { slidesPerView: 3.2, spaceBetween: 16 },
                            1280: { slidesPerView: 4.2, spaceBetween: 16 },
                        }}
                        className="product_slider"
                    >
                        {products && products?.length > 0 && products?.map((item) => (
                            <SwiperSlide key={item?._id}>
                                {recentlyViewed ? <ProductCard data={(item as IRecentlyViewedProductAPIResponseDataEntity).product} /> : <ProductCard data={item as INewArrivalsAPIResponseDataEntity} />}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>
        </>
    )
}
